<script lang="ts">
  import { onMount } from "svelte"
  import Layout from "$app/Layouts/Plan.svelte"
  import { Pulse } from "svelte-loading-spinners"
  import ConfettiBurst from "$app/components/Confetti/ConfettiBurst.svelte"
  import {
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    Download,
    MoveRight
  } from "lucide-svelte"
  import Dropdown from "$app/components/Dropdown.svelte"
  import { xhr, toast } from "$app/utils"
  import TopRecognitions from "$app/Pages/Plans/Meetings/TopRecognitions.svelte"
  import PlanReview from "$app/Pages/Plans/Meetings/PlanReview.svelte"
  import AnnualReview from "./AnnualReview.svelte"
  import QuarterReview from "./QuarterReview.svelte"
  import SwotReview from "./SwotReview.svelte"
  import OnePageReview from "./OnePageReview.svelte"
  import Modal from "$app/components/Modal.svelte"
  import Nav from "./Nav.svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    plan: PlanType
    meeting: MeetingType
    dates: DatesType
    meeting_type: string
    top_recognitions?: any
    recognition_stats?: any
    plan_review?: any
    plan_visions?: { next_quarter: { vision: string } }
    step: number
    departments?: { [key: number]: DepartmentType }
    users: UserType[]
    headings: any[]
    goal_data?: GoalType[]
    task_data?: TaskTypeE[]
    onboarding: any
    current_user: UserType
    update_core?: boolean
    update_goals?: boolean
    statements?: StatementType[]
  }

  let props = $props()
  let {
    plan,
    meeting,
    dates,
    meeting_type,
    top_recognitions = [],
    recognition_stats = {},
    plan_review = null,
    plan_visions = { next_quarter: { vision: "" } },
    step,
    departments,
    users,
    headings,
    goal_data = [],
    task_data = [],
    onboarding,
    current_user,
    update_core = false,
    update_goals = false,
    statements = []
  }: Props = props

  let openDropdown = $state(false)
  let showConfetti = $state(false)
  let currentStepMod: any = $state()

  let innerWidth = $state(window.innerWidth)
  let innerHeight = $state(window.innerHeight)

  appStore.plan = plan
  appStore.onboarding = onboarding
  appStore.users = users
  appStore.departments = Object.values(departments)
  appStore.dates = dates
  appStore.current_user = current_user
  appStore.statements = statements
  appStore.meeting = meeting
  appStore.channel_id = `meeting_goals_${meeting.uniq_id}`
  appStore.meeting_type = meeting_type
  appStore.update_goals = update_goals
  appStore.update_core = update_core
  appStore.statements = statements

  let depts: DepartmentType[] = Object.values(departments).sort((a, b) => {
    return a.position - b.position
  })

  let showCompletedModal = $state(false)

  let loading = $state(true)

  onMount(() => {
    departments = Object.values(departments).map((d) => {
      return { ...d, curr_expanded: true, next_expanded: true }
    })

    appStore.goals = goal_data.map((g) => {
      return { ...g, _toggle: false, _selected: false }
    })

    if (plan_review?.statements) {
      appStore.statements = plan_review.statements.map((st) => {
        return { ...st, _selected: false }
      }) as StatementType[]
    }

    appStore.tasks = task_data || []
    loading = false
  })

  async function setCompleted(): Promise<void> {
    try {
      const resp = await xhr.patch(
        `/plans/${plan.id}/meetings/${meeting.id}.json`,
        { meeting: { completed: true, current_step: step } }
      )

      showConfetti = true
      showCompletedModal = true
    } catch (e) {
      console.error(e)
      toast("Error marking transition complete", "error")
    }
  }

  const stepsCount = meeting_type === "annual" ? 6 : 5

  function moveNext(): void {
    if (!currentStepMod.valid()) {
      toast(currentStepMod.getErrorMessage(), "error")
    } else {
      if (window.location.href.indexOf("step") > -1) {
        const url = window.location.href.replace(
          `step=${step}`,
          `step=${step + 1}`
        )
        window.location.href = url
      } else {
        window.location.href = `${window.location.href}?step=${step + 1}`
      }
    }
  }

  const addAnnualStep = meeting_type === "annual" ? 1 : 0
</script>

<svelte:window bind:innerWidth bind:innerHeight />

<Layout {...props} plan_users={Object.values(users)}>
  <div class="relative max-w-7xl mx-auto sm:px-6 lg:px-8 pb-8">
    <div class="absolute top-5 right-10 text-left ml-2 items-center p-1"></div>
    <div class="max-w-3xl mx-auto px-4 lg:max-w-7xl py-3">
      <div class="flex items-center justify-between flex-wrap py-4">
        <div class="flex justify-start items-center">
          {#if meeting_type === "quarterly"}
            <p class="text-3xl font-bold">Quarter Transition</p>
          {:else if meeting_type === "annual"}
            <p class="text-3xl font-bold whitespace-nowrap">
              Annual Transition
            </p>
          {/if}
          <div
            class="my-2 ml-4 bg-white font-semibold rounded-full text-lg py-2 px-8 text-center text-gray-700 border-gray-200 shadow-sm flex items-center whitespace-nowrap"
          >
            {dates.prev_quarter}
            <MoveRight class="w-8 h-8 mx-4 text-gray-500" />
            {dates.next_quarter}
          </div>
        </div>
        <div class="flex items-center justify-center">
          <Dropdown bind:open={openDropdown}>
            {#snippet button()}
              <span
                class="py-2 px-4 flex rounded text-sm text-gray-900 bg-green-50 border border-gray-300 items-center"
              >
                Download Transition Checklists
                <ChevronDown class="ml-1 text-gray-500 w-6 h-6" />
              </span>
            {/snippet}

            <div>
              <div
                class="origin-top-right absolute right-0 mt-1 w-64 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 focus:outline-none z-10"
                role="menu"
              >
                <div class="py-1" role="none">
                  <a
                    href="https://leadwithpurpose-web.s3.amazonaws.com/resources/LWP-Quarterly-Transition-Checklist.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn-dropdown"
                    role="menuitem"
                  >
                    <Download class="text-black w-4 h-4 mr-2" />
                    Quarterly Transition Checklist
                  </a>
                  <a
                    href="https://leadwithpurpose-web.s3.amazonaws.com/resources/LWP-Annual-Transition-Checklist.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn-dropdown"
                    role="menuitem"
                  >
                    <Download class="text-black w-4 h-4 mr-2" />
                    Annual Transition Checklist
                  </a>
                </div>
              </div>
            </div>
          </Dropdown>
        </div>
      </div>
    </div>

    <Nav {step} />

    {#if showConfetti}
      <ConfettiBurst
        particleCount={300}
        origin={[innerWidth / 2, innerHeight / 4]}
      />
    {/if}

    <main class="py-10 lg:px-0 px-3">
      {#if loading}
        <div class="py-12 flex justify-center w-full">
          <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
        </div>
      {:else if step === 1}
        <TopRecognitions
          {dates}
          {top_recognitions}
          {recognition_stats}
          bind:this={currentStepMod}
        />
      {:else if step === 2}
        <PlanReview
          bind:this={currentStepMod}
          editable={true}
          actionCable={true}
        >
          {#snippet header()}
            <h2
              class="text-xl text-center w-full py-4 font-semibold bg-indigo-50 text-indigo-800 rounded-sm shadow px-4"
            >
              Review and make changes to your plan
            </h2>
          {/snippet}
        </PlanReview>
      {:else if step === 3 && meeting_type === "annual"}
        <AnnualReview {plan_visions} bind:this={currentStepMod} />
      {:else if step === 3 + addAnnualStep}
        <QuarterReview {plan_visions} bind:this={currentStepMod} />
      {:else if step === 4 + addAnnualStep}
        <SwotReview {headings} {dates} bind:this={currentStepMod} />
      {:else if step === 5 + addAnnualStep}
        <OnePageReview
          {headings}
          {dates}
          {goal_data}
          bind:this={currentStepMod}
        />
      {:else if step >= 6 + addAnnualStep}
        <div class="bg-white w-full p-8">
          <p class="text-green-800 text-xl font-bold text-center">
            Congratulations on completing this transition meeting
          </p>
        </div>
      {/if}
    </main>

    <div class="py-6 flex justify-center md:justify-end">
      {#if step > 1}
        <a
          href="?step={step - 1}"
          class="btn btn-rounded secondary btn-big mr-4"
        >
          <ChevronLeft class="w-4 h-4 mr-2" />
          Previous
        </a>
      {/if}

      {#if step < stepsCount}
        <button
          onclick={moveNext}
          class="btn btn-rounded success btn-big"
          data-name="next-button"
        >
          Next
          <ChevronRight class="w-4 h-4 ml-2" />
        </button>
      {:else}
        <button
          class="btn btn-rounded success btn-big"
          onclick={setCompleted}
          data-name="finish-button"
        >
          FINISH
        </button>
      {/if}
    </div>
  </div>

  <Modal
    bind:open={showCompletedModal}
    closeButton={true}
    closeOnEsc={false}
    closeOnOuterClick={false}
    hasBackdrop={false}
  >
    <div class="mx-auto py-8" data-name="final-modal">
      <h2 class="text-3xl text-center">Congratulations!</h2>
      <p
        class="mt-4 text-2xl text-gray-800 text-center"
        data-name="final-message"
      >
        Your plan has been updated for {dates.next_quarter}
      </p>
      <div class="mt-8 flex justify-center">
        <a
          href={"/plans/" + plan.id}
          class="btn btn-big success"
          onclick={() => (showCompletedModal = false)}
        >
          Done
        </a>
      </div>
    </div>
  </Modal>
</Layout>
